/**
 * Data layer for `igaming-per-brand-responsible-gambling-filters`. Thin typed fetch wrappers over /api/v1/igaming-per-brand-responsible-gambling-filters.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingPerBrandResponsibleGamblingFiltersRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingPerBrandResponsibleGamblingFiltersResponse {
	count: number;
	items: IgamingPerBrandResponsibleGamblingFiltersRow[];
}

export interface CreateIgamingPerBrandResponsibleGamblingFiltersBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-per-brand-responsible-gambling-filters";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingPerBrandResponsibleGamblingFiltersResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingPerBrandResponsibleGamblingFiltersResponse>(res);
}

export async function create(body: CreateIgamingPerBrandResponsibleGamblingFiltersBody): Promise<IgamingPerBrandResponsibleGamblingFiltersRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingPerBrandResponsibleGamblingFiltersRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingPerBrandResponsibleGamblingFiltersQueryKeys = {
	all: ["igaming-per-brand-responsible-gambling-filters"] as const,
	list: () => [...igamingPerBrandResponsibleGamblingFiltersQueryKeys.all] as const,
	detail: (id: string) => [...igamingPerBrandResponsibleGamblingFiltersQueryKeys.all, id] as const,
};
